import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Dices, Play, Loader2, AlertTriangle, TrendingUp, TrendingDown, FileSpreadsheet, FileText, Lock, History, Landmark } from "lucide-react";
import HelpButton from "@/components/HelpButton";
import McJobProgress from "@/components/McJobProgress";
import BudgetDeviationBlock from "@/components/BudgetDeviationBlock";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, ReferenceLine, LineChart, Line, Area, ComposedChart, Legend } from "recharts";
import { api, API_BASE, tokenStore, formatApiError, fmtInt, fmtCurrency } from "@/lib/api";
import { saveOrDownload, useDownloadFolderName } from "@/lib/downloadFolder";
import { isActiveJob, trackJob, untrackJob } from "@/lib/mcJobs";

const POLL_MS = 1000;
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

// Zet de variabelen uit een eerder opgeslagen job-payload terug in de configuratietabel.
function applyPayloadToVars(vars, payload) {
  if (!payload?.variables) return vars;
  const byKey = Object.fromEntries(payload.variables.map((v) => [v.key, v]));
  return vars.map((v) => {
    const p = byKey[v.key];
    return p ? { ...v, enabled: true, distribution: p.distribution || v.distribution, low: p.low ?? v.low, base: p.base ?? v.base, high: p.high ?? v.high }
             : { ...v, enabled: false };
  });
}

// -----------------------------------------------------------------------------
// Monte Carlo — Fase A (MVP)
// -----------------------------------------------------------------------------
// Configuration UI: pick which core variables to randomize and their distribution.
// Runs POST /api/scenarios/{sid}/monte-carlo → renders NPV histogram + KPIs.
// -----------------------------------------------------------------------------

// Default variables. Users can toggle each on/off and pick distribution.
const DEFAULT_VARS = [
  { key: "inflation_rate",       label: "Inflatie (algemeen)",  default_base: 0.03, low_hint: 0.01,  high_hint: 0.06 },
  { key: "revenue_growth_rate",  label: "Groei omzet",           default_base: 0.03, low_hint: 0.00,  high_hint: 0.06 },
  { key: "opex_growth_rate",     label: "Groei kosten",          default_base: 0.03, low_hint: 0.01,  high_hint: 0.08 },
  { key: "discount_rate",        label: "Disconteringsvoet",     default_base: 0.075, low_hint: 0.05, high_hint: 0.10 },
];

function initialConfigFromScenario(scenario) {
  const s = scenario || {};
  return DEFAULT_VARS.map((v) => {
    const currentBase = s[v.key];
    const base = (typeof currentBase === "number" && !Number.isNaN(currentBase)) ? currentBase : v.default_base;
    // Reasonable ±40% band as default triangular. Rounded to nearest 0.005 so
    // percentages display cleanly (e.g. 3% → 1.8/3/4.2 instead of FP noise).
    const spread = Math.max(0.005, Math.abs(base) * 0.4);
    const round = (x) => Math.round(x * 1000) / 1000;
    return {
      key: v.key,
      label: v.label,
      enabled: true,
      distribution: "triangular",
      low: round(Math.max(0, base - spread)),
      base: round(base),
      high: round(base + spread),
    };
  });
}

function investmentPlanVars(scenario) {
  const plans = scenario?.scenario_future_investments || [];
  const out = [];
  const devVars = [];
  plans.forEach((p, idx) => {
    const label = p.name || `Investering #${idx + 1}`;
    if (Number(p.amount) > 0) {
      out.push({ key: `invplan.${idx}.amount`, label: `${label} · bedrag`, default_base: Number(p.amount) || 0, enabled: false });
      devVars.push({ key: `invplan.${idx}.budget_dev`, label: `${label} · budgetafwijking`, enabled: false, distribution: "split_normal", low: -0.05, base: 0, high: 0.25 });
    }
    if (Number(p.additional_revenue) > 0) {
      out.push({ key: `invplan.${idx}.additional_revenue`, label: `${label} · extra omzet/jr`, default_base: Number(p.additional_revenue) || 0, enabled: false });
    }
    if (Number(p.additional_costs) > 0) {
      out.push({ key: `invplan.${idx}.additional_costs`, label: `${label} · extra kosten/jr`, default_base: Number(p.additional_costs) || 0, enabled: false });
    }
    if (Number(p.loan_amount) > 0) {
      out.push({ key: `invplan.${idx}.loan_amount`, label: `${label} · lening`, default_base: Number(p.loan_amount) || 0, enabled: false });
    }
  });
  return [...out.map((v) => {
    const base = v.default_base;
    const spread = Math.max(500, Math.abs(base) * 0.2);
    return {
      key: v.key,
      label: v.label,
      enabled: false,
      distribution: "triangular",
      low: Math.max(0, base - spread),
      base,
      high: base + spread,
    };
  }), ...devVars];
}

export const isRatioKey = (key) => key.includes("rate") || key.includes("growth") || key.endsWith("budget_dev");

export default function MonteCarloPanel({ t, scenario, sid, clubId }) {
  const [vars, setVars] = useState(() => [
    ...initialConfigFromScenario(scenario),
    ...investmentPlanVars(scenario),
  ]);
  const [nRuns, setNRuns] = useState(5000);
  const [cashFloor, setCashFloor] = useState(50000);
  const [floorInfo, setFloorInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [job, setJob] = useState(null);
  const [queue, setQueue] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState("");
  const [tier, setTier] = useState(null);
  const [exportBusy, setExportBusy] = useState("");
  const folderName = useDownloadFolderName(clubId);
  const restoredRef = useRef(false);

  useEffect(() => {
    let alive = true;
    api.get("/auth/me").then(({ data }) => { if (alive) setTier(data?.tier || "FREE"); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Bewaard resultaat of lopende taak van dit scenario terughalen (ook na pagina-wissel).
  useEffect(() => {
    let alive = true;
    api.get(`/scenarios/${sid}/monte-carlo/jobs/latest`).then(({ data }) => {
      if (!alive || !data?.job) return;
      const j = data.job;
      setQueue(data.queue);
      setJob(j);
      if (isActiveJob(j)) trackJob({ id: j.id, scenarioId: sid, clubId, scenarioName: j.scenario_name });
      if (j.status === "done" && j.result) setResult(j.result);
      if (j.payload && !restoredRef.current) {
        restoredRef.current = true;
        setVars((prev) => applyPayloadToVars(prev, j.payload));
        if (j.payload.n_runs) setNRuns(Number(j.payload.n_runs));
      }
    }).catch(() => {});
    // Ondergrens eigen kas: opgeslagen keuze bij het scenario, anders het voorstel voor deze club.
    api.get(`/scenarios/${sid}/monte-carlo/cash-floor`).then(({ data }) => {
      if (!alive || !data) return;
      setFloorInfo(data);
      setCashFloor(Math.round(data.saved ?? data.suggested ?? 50000));
    }).catch(() => {});
    return () => { alive = false; };
  }, [sid, clubId]);

  // Voortgang pollen zolang de taak loopt.
  const jobActive = isActiveJob(job);
  useEffect(() => {
    if (!jobActive) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const [{ data: j }, { data: q }] = await Promise.all([api.get(`/monte-carlo/jobs/${job.id}`), api.get("/monte-carlo/queue")]);
        if (!alive) return;
        setQueue(q);
        setJob(j);
        if (j.status === "done") { setResult(j.result); setLoading(false); }
        else if (j.status === "failed") { setError(j.error || t("Monte Carlo mislukt.")); setLoading(false); }
        else if (j.status === "cancelled") { setLoading(false); toast.info(t("Simulatie geannuleerd.")); }
      } catch (e) {
        if (alive && e.response?.status === 404) { setJob(null); setLoading(false); }
      }
    };
    const timer = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(timer); };
  }, [jobActive, job?.id, t]);

  const isUltimate = tier === "PREMIUM" || tier === "TRIAL";
  const tierLabel = tier === "TRIAL" ? "TRIAL" : "PREMIUM";
  const enabledCount = useMemo(() => vars.filter((v) => v.enabled).length, [vars]);

  // Label-map: backend key → Dutch label from the config table (or the key itself for per-plan overrides).
  const varLabels = useMemo(() => {
    const out = {};
    vars.forEach((v) => { out[v.key] = v.label; });
    return out;
  }, [vars]);

  // Start year for fan chart X-axis: from scenario or current year.
  const startYear = Number(scenario?.start_year) || new Date().getFullYear();

  const patch = (idx, key, val) => setVars((prev) => prev.map((v, i) => (i === idx ? { ...v, [key]: val } : v)));

  const buildPayload = () => ({
    n_runs: nRuns,
    budget_cash_floor: Math.max(0, Number(cashFloor) || 0),
    variables: vars.filter((v) => v.enabled).map((v) => ({
      key: v.key,
      distribution: v.distribution,
      low: Number(v.low) || 0,
      base: Number(v.base) || 0,
      high: Number(v.high) || 0,
    })),
  });

  const run = async () => {
    setLoading(true); setError(""); setResult(null);
    const payload = buildPayload();
    try {
      const { data } = await api.post(`/scenarios/${sid}/monte-carlo/jobs`, payload);
      setJob(data);
      trackJob({ id: data.id, scenarioId: sid, clubId, scenarioName: data.scenario_name || scenario?.name });
      if (data.existing) toast.info(t("Er loopt al een simulatie voor dit scenario — de voortgang wordt getoond."));
      else if (data.queue_position > 1) toast.info(t("In de wachtrij gezet (positie {p}).").replace("{p}", data.queue_position));
    } catch (e) {
      const status = e.response?.status;
      const msg = status === 402 ? t("Deze functie vereist het PREMIUM-abonnement.") :
                  formatApiError(e.response?.data?.detail) || e.message || t("Monte Carlo mislukt.");
      setError(msg); toast.error(msg); setLoading(false);
    }
  };

  const cancel = useCallback(async () => {
    if (!job) return;
    setCancelling(true);
    try { const { data } = await api.delete(`/monte-carlo/jobs/${job.id}`); setJob(data); untrackJob(job.id); if (data.status === "cancelled") setLoading(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("Annuleren mislukt.")); }
    finally { setCancelling(false); }
  }, [job, t]);

  const exportFile = async (kind /* "xlsx" | "pdf" */) => {
    if (!job || job.status !== "done") { toast.error(t("Draai eerst een simulatie.")); return; }
    setExportBusy(kind);
    try {
      const resp = await fetch(`${API_BASE}/monte-carlo/jobs/${job.id}/export.${kind}`, {
        headers: { Authorization: `Bearer ${tokenStore.get() || ""}` },
      });
      if (!resp.ok) throw new Error(await resp.text() || "Export failed");
      const blob = await resp.blob();
      const suggestedName = `monte-carlo-${(result?.scenario_name || "scenario").replace(/[^a-z0-9]+/gi, "_").toLowerCase()}.${kind}`;
      await saveOrDownload(blob, suggestedName, clubId);
      toast.success(t("Export gereed") + (folderName ? ` · ${folderName}` : ""));
    } catch (e) {
      toast.error(t("Export mislukt.") + " " + (e.message || ""));
    } finally {
      setExportBusy("");
    }
  };

  const busy = loading || jobActive;
  const canExport = !!result && job?.status === "done" && !exportBusy && isUltimate;

  return (
    <div className="bg-card border-2 border-foreground heavy-shadow mt-8 overflow-hidden relative" data-testid="monte-carlo-panel">
      <div className="p-5 border-b-2 border-foreground flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Dices className="w-7 h-7 text-primary" />
          <div>
            <div className="stat-label flex items-center gap-2">
              {t("Onzekerheids-analyse")}
              {tier === null ? (
                <span className="text-[10px] px-1.5 py-0.5 font-bold uppercase bg-muted text-muted-foreground border border-border" data-testid="mc-tier-badge">
                  …
                </span>
              ) : (
                <span className={`text-[10px] px-1.5 py-0.5 font-bold uppercase ${isUltimate ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground border border-border"}`} data-testid="mc-tier-badge">
                  {isUltimate ? tierLabel : t("PREMIUM vereist")}
                </span>
              )}
            </div>
            <h2 className="font-display font-black text-2xl uppercase tracking-tight">{t("Risicosimulatie (Monte Carlo)")}</h2>
          </div>
          <HelpButton section="monte-carlo" label="Uitleg risicosimulatie (Monte Carlo)" size="sm" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs font-display uppercase tracking-widest flex items-center gap-2">
            {t("Simulaties")}
            <select value={nRuns} onChange={(e) => setNRuns(parseInt(e.target.value, 10))}
                    data-testid="mc-n-runs"
                    className="border-2 border-foreground bg-background px-2 py-1 font-mono-num">
              <option value={1000}>1.000</option>
              <option value={5000}>5.000</option>
              <option value={10000}>10.000</option>
            </select>
          </label>
          <button onClick={run} disabled={busy || enabledCount === 0 || !isUltimate} data-testid="mc-run-btn"
                  className={`btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 ${(busy || enabledCount === 0 || !isUltimate) ? "opacity-50 cursor-not-allowed" : ""}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {busy ? (job?.status === "queued" ? t("In wachtrij…") : t("Simuleren…")) : t("Simulatie starten")}
          </button>
          <button onClick={() => exportFile("xlsx")} disabled={!canExport} data-testid="mc-export-xlsx"
                  className={`btn-brutal bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-xs inline-flex items-center gap-2 ${!canExport ? "opacity-40 cursor-not-allowed" : ""}`}>
            {exportBusy === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            Excel
          </button>
          <button onClick={() => exportFile("pdf")} disabled={!canExport} data-testid="mc-export-pdf"
                  className={`btn-brutal bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-xs inline-flex items-center gap-2 ${!canExport ? "opacity-40 cursor-not-allowed" : ""}`}>
            {exportBusy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            PDF
          </button>
        </div>
      </div>

      {queue && (queue.running > 0 || queue.queued > 0) && !jobActive && (
        <div className="px-5 py-2 border-b-2 border-foreground bg-muted/40 text-[11px] font-mono-num flex items-center gap-2" data-testid="mc-queue-info">
          <History className="w-3.5 h-3.5" />
          {t("Wachtrij server-breed")}: {queue.running} {t("lopend")} · {queue.queued} {t("wachtend")} — {t("één simulatie tegelijk, in volgorde van aanvraag")}
        </div>
      )}

      {!isUltimate && tier !== null && (
        <div className="p-5 bg-secondary/10 border-b-2 border-secondary" data-testid="mc-tier-lock">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-secondary mt-0.5" />
            <div>
              <p className="font-display uppercase font-bold text-sm">{t("PREMIUM-abonnement vereist")}</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                {t("Monte Carlo simulaties zitten in het PREMIUM-abonnement. Start een 14-daagse proef om deze functie op de demo-club uit te proberen, of upgrade om er in je eigen scenario's mee te werken.")}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className={`p-5 space-y-4 ${!isUltimate ? "opacity-60 pointer-events-none" : ""}`}>
        <p className="text-xs text-muted-foreground max-w-3xl">
          {t("Selecteer welke variabelen mogen fluctueren, kies een verdeling met minimum / basis / maximum, en start de simulatie. Elke run herrekent het scenario opnieuw en het paneel toont vervolgens de kansverdeling van de netto contante waarde (NPV).")}
        </p>

        {/* --- Variables config --- */}
        <div className="border-2 border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-foreground text-background">
                <th className="text-left px-3 py-2 font-display uppercase text-[10px] tracking-widest">{t("Aan")}</th>
                <th className="text-left px-3 py-2 font-display uppercase text-[10px] tracking-widest">{t("Variabele")}</th>
                <th className="text-left px-3 py-2 font-display uppercase text-[10px] tracking-widest">{t("Verdeling")}</th>
                <th className="text-right px-3 py-2 font-display uppercase text-[10px] tracking-widest">{t("Min")}</th>
                <th className="text-right px-3 py-2 font-display uppercase text-[10px] tracking-widest">{t("Basis")}</th>
                <th className="text-right px-3 py-2 font-display uppercase text-[10px] tracking-widest">{t("Max")}</th>
              </tr>
            </thead>
            <tbody>
              {vars.map((v, idx) => (
                <VarRow key={v.key} v={v} idx={idx} patch={patch} t={t} />
              ))}
            </tbody>
          </table>
        </div>

        {vars.some((v) => v.enabled && v.key.endsWith("budget_dev")) && (
          <div className="border-2 border-secondary bg-secondary/5 p-3 flex items-start gap-3 flex-wrap" data-testid="mc-cash-floor-block">
            <Landmark className="w-4 h-4 mt-1 shrink-0" />
            <div className="flex-1 min-w-[240px]">
              <p className="font-display uppercase font-bold text-xs tracking-widest">{t("Kies zelf de ondergrens eigen kas bij meerkosten")}</p>
              <p className="text-[11px] text-muted-foreground leading-snug">{t("Meerkosten komen uit eigen kas. Zakt de kaspositie in het uitvoeringsjaar onder deze grens, dan wordt het tekort (max. de meerkosten) geleend tegen de leningvoorwaarden van het project — zonder lening tegen de standaard rente/looptijd van het project.")}</p>
              {floorInfo && (
                <p className="text-[11px] mt-1 leading-snug" data-testid="mc-cash-floor-suggestion">
                  {floorInfo.basis_year && floorInfo.annual_costs > 0 ? (
                    <>{t("Voorstel voor deze club")}: <b className="font-mono-num">{fmtCurrency(floorInfo.suggested)}</b> <span className="text-muted-foreground">({t("2 maanden uitgaven o.b.v. jaarcijfers")} {floorInfo.basis_year}: {fmtCurrency(floorInfo.annual_costs)}/{t("jaar")}, {t("afgerond op € 5.000")})</span></>
                  ) : (
                    <span className="text-muted-foreground">{t("Geen jaarcijfers gevonden — standaard")} {fmtCurrency(floorInfo.suggested)}; {t("pas aan naar de omvang van je club")}.</span>
                  )}
                  {Number(cashFloor) !== Math.round(floorInfo.suggested) && (
                    <button type="button" onClick={() => setCashFloor(Math.round(floorInfo.suggested))} data-testid="mc-cash-floor-use-suggestion"
                            className="ml-2 underline underline-offset-2 font-bold hover:text-primary">{t("Gebruik voorstel")}</button>
                  )}
                  {floorInfo.saved != null && Number(cashFloor) === Math.round(floorInfo.saved) && <span className="ml-2 text-muted-foreground italic">{t("jouw opgeslagen keuze")}</span>}
                </p>
              )}
            </div>
            <label className="text-xs font-display uppercase tracking-widest flex items-center gap-2">
              €
              <input type="text" inputMode="numeric" value={cashFloor}
                     onChange={(e) => setCashFloor(e.target.value.replace(/[^\d]/g, ""))}
                     onBlur={() => setCashFloor(Math.max(0, parseInt(cashFloor, 10) || 0))}
                     data-testid="mc-cash-floor"
                     className="border-2 border-foreground bg-background px-3 py-2 font-mono-num font-bold text-base w-36 text-right" />
            </label>
          </div>
        )}

        {error && (
          <div className="p-3 bg-destructive/10 border-2 border-destructive text-destructive text-sm" data-testid="mc-error">{error}</div>
        )}

        {jobActive && <McJobProgress t={t} job={job} queue={queue} onCancel={cancel} cancelling={cancelling} />}

        {result && job?.status === "done" && job.finished_at && (
          <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5" data-testid="mc-result-meta">
            <History className="w-3.5 h-3.5" /> {t("Resultaat van")} {fmtTime(job.finished_at)}
            {job.duration_s != null && <> · {t("rekentijd")} {Math.round(job.duration_s)} s</>}
            {" · "}{t("bewaard tot 7 dagen na de run")}
          </p>
        )}

        {result && (
          <p className="text-xs text-muted-foreground" data-testid="mc-discount-rate-note">
            {t("NCW (NPV) berekend tegen disconteringsvoet")} <span className="font-mono-num font-bold text-foreground">{((Number(scenario?.discount_rate) || 0) * 100).toFixed(1).replace(".", ",")} %</span>{" "}
            {t("(scenario-instelling, aan te passen in de bouwer; hieronder als variabele te laten variëren)")}
          </p>
        )}
        {result && <ResultsBlock t={t} result={result} varLabels={varLabels} startYear={startYear} />}
      </div>
    </div>
  );
}

function VarRow({ v, idx, patch, t }) {
  const isRatio = isRatioKey(v.key);
  const isDev = v.key.endsWith("budget_dev");
  const split = v.distribution === "split_normal";
  const step = isRatio ? 0.005 : 100;
  const lowLabel = split ? "P10" : undefined;
  const baseLabel = v.distribution === "normal" ? "μ" : split ? t("modus") : undefined;
  const highLabel = v.distribution === "normal" ? "σ" : split ? "P90" : undefined;
  return (
    <tr className={`border-t border-border ${isDev ? "bg-secondary/5" : ""}`}>
      <td className="px-3 py-2">
        <input type="checkbox" checked={v.enabled}
               onChange={(e) => patch(idx, "enabled", e.target.checked)}
               data-testid={`mc-var-${v.key}-toggle`} />
      </td>
      <td className="px-3 py-2 font-medium">
        {v.label}
        {isDev && <div className="text-[10px] text-muted-foreground font-normal leading-snug max-w-[260px]">{t("Onder-/overschrijding van het budget in %. Meerkosten komen uit eigen kas; onder de ondergrens wordt het tekort geleend (zie hieronder).")}</div>}
      </td>
      <td className="px-3 py-2">
        <select value={v.distribution} onChange={(e) => patch(idx, "distribution", e.target.value)}
                disabled={!v.enabled}
                data-testid={`mc-var-${v.key}-dist`}
                className="border border-input bg-background px-2 py-1 text-xs">
          {isDev && <option value="split_normal">{t("Asymmetrisch normaal (P10 / P90)")}</option>}
          <option value="triangular">{t("Triangulair")}</option>
          <option value="uniform">{t("Uniform")}</option>
          <option value="normal">{t("Normaal (μ,σ)")}</option>
          {!isDev && <option value="split_normal">{t("Asymmetrisch normaal (P10 / P90)")}</option>}
          <option value="fixed">{t("Vast")}</option>
        </select>
      </td>
      <td className="px-3 py-2 text-right">
        <NumField value={v.low} step={step} onChange={(val) => patch(idx, "low", val)} disabled={!v.enabled || v.distribution === "fixed" || v.distribution === "normal"} testid={`mc-var-${v.key}-low`} isRatio={isRatio} label={lowLabel} />
      </td>
      <td className="px-3 py-2 text-right">
        <NumField value={v.base} step={step} onChange={(val) => patch(idx, "base", val)} disabled={!v.enabled} testid={`mc-var-${v.key}-base`} isRatio={isRatio} label={baseLabel} />
      </td>
      <td className="px-3 py-2 text-right">
        <NumField value={v.high} step={step} onChange={(val) => patch(idx, "high", val)} disabled={!v.enabled || v.distribution === "fixed"} testid={`mc-var-${v.key}-high`} isRatio={isRatio} label={highLabel} />
      </td>
    </tr>
  );
}

function NumField({ value, step, onChange, disabled, testid, isRatio, label }) {
  // Ratios display as percentages for user friendliness.
  const asPct = (n) => Number.isFinite(n) ? Math.round(n * 10000) / 100 : 0;   // round to 2 decimals to avoid FP noise
  const display = isRatio ? asPct(Number(value)) : Math.round(Number(value) || 0);
  const [raw, setRaw] = useState(String(display));
  useEffect(() => { setRaw(String(display)); }, [display]);
  return (
    <span className="inline-flex items-center gap-1">
      {label && <span className="text-[10px] uppercase text-muted-foreground">{label}</span>}
      <input type="text" inputMode="decimal"
             value={raw}
             disabled={disabled}
             onChange={(e) => setRaw(e.target.value)}
             onBlur={() => {
               const n = parseFloat(String(raw).replace(",", "."));
               if (Number.isFinite(n)) {
                 const commit = isRatio ? (n / 100) : n;
                 onChange(commit);
                 setRaw(String(isRatio ? n : Math.round(commit)));
               } else {
                 setRaw(String(display));
               }
             }}
             onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
             data-testid={testid}
             className={`w-24 font-mono-num text-right px-2 py-1 border border-input bg-background ${disabled ? "opacity-50" : ""}`} />
      {isRatio && <span className="text-[10px] text-muted-foreground">%</span>}
    </span>
  );
}

function ResultsBlock({ t, result, varLabels, startYear }) {
  const { npv, histogram, prob_cash_shortfall, payback, runs, tornado, fan_chart } = result;
  const chartData = histogram.bin_centers.map((c, i) => ({
    center: c,
    count: histogram.counts[i],
    isNegative: c < 0,
  }));

  const probNegPct = (npv.prob_negative * 100).toFixed(1);
  const probShortPct = (prob_cash_shortfall * 100).toFixed(1);

  return (
    <div className="space-y-4" data-testid="mc-results">
      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi t={t} label="NCW (NPV) P10 (pessimistisch)"    value={npv.p10} tone="down" testid="mc-kpi-p10" />
        <Kpi t={t} label="NCW (NPV) P50 (mediaan)"          value={npv.p50} tone="neutral" testid="mc-kpi-p50" />
        <Kpi t={t} label="NCW (NPV) P90 (optimistisch)"     value={npv.p90} tone="up" testid="mc-kpi-p90" />
        <Kpi t={t} label="Gemiddelde ± std.dev"       value={npv.mean} suffix={` ± ${fmtCurrency(npv.stdev)}`} testid="mc-kpi-mean" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <RiskCard t={t} label={t("Kans op negatieve netto contante waarde (NPV)")} pct={probNegPct} testid="mc-risk-npv" />
        <RiskCard t={t} label={t("Kans op cash-tekort tijdens horizon")} pct={probShortPct} testid="mc-risk-cash" />
      </div>

      {/* Histogram */}
      <div className="border-2 border-foreground p-3 bg-card" data-testid="mc-histogram">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <p className="font-display uppercase font-bold text-xs tracking-widest">{t("Verdeling netto contante waarde (NPV)")} ({runs.toLocaleString("nl-NL")} {t("simulaties")})</p>
          <p className="text-[10px] text-muted-foreground italic">
            {t("Blauw = positief · Rood = negatief · Verticale lijn = mediaan")}
          </p>
        </div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
              <XAxis dataKey="center" type="number" domain={["dataMin", "dataMax"]}
                     tick={{ fontSize: 10, fontFamily: "monospace" }}
                     tickFormatter={(v) => fmtCurrency(v)} />
              <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} width={40} />
              <Tooltip formatter={(v) => [`${v} runs`, t("Frequentie")]} labelFormatter={(v) => `NPV ≈ ${fmtInt(v)}`} />
              <ReferenceLine x={npv.p50} stroke="#000" strokeDasharray="4 3" label={{ value: "P50", position: "top", fontSize: 10 }} />
              <ReferenceLine x={0} stroke="#FF5900" strokeWidth={2} />
              <Bar dataKey="count">
                {chartData.map((d, i) => (
                  <Cell key={`cell-${d.bin ?? i}`} fill={d.isNegative ? "#FF5900" : "#0047FF"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tornado chart */}
      {tornado && tornado.length > 0 && <TornadoChart t={t} tornado={tornado} varLabels={varLabels} />}

      {/* Budgetafwijking → extra lenen */}
      {result.budget_deviation && <BudgetDeviationBlock t={t} data={result.budget_deviation} />}

      {/* Fan chart */}
      {fan_chart && fan_chart.length > 0 && <FanChart t={t} fanChart={fan_chart} startYear={startYear} cashFloor={result.budget_deviation?.cash_floor} />}

      {/* Payback distribution summary */}
      {payback && payback.p50 !== null && (
        <div className="text-xs text-muted-foreground" data-testid="mc-payback-summary">
          {t("Terugverdientijd (mediaan)")}: <strong className="font-mono-num text-foreground">{payback.p50.toFixed(1)}j</strong>
          {" · "}
          {t("P10 (snelst)")}: <span className="font-mono-num">{payback.p10.toFixed(1)}j</span>
          {" · "}
          {t("P90 (langzaamst)")}: <span className="font-mono-num">{payback.p90.toFixed(1)}j</span>
          {payback.prob_no_payback > 0 && (
            <span className="text-destructive">
              {" · "}{t("Geen terugverdientijd")}: {(payback.prob_no_payback * 100).toFixed(1)}%
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function TornadoChart({ t, tornado, varLabels }) {
  // Recharts horizontal bar chart, sorted by |correlation| (already sorted by backend).
  // Positive correlations = blue, negative = orange (Dutch UX convention same as histogram).
  const data = tornado.map((row) => ({
    ...row,
    absCorr: Math.abs(row.correlation),
    label: varLabels[row.key] || row.key,
  }));
  const maxHeight = Math.max(120, data.length * 34 + 40);
  return (
    <div className="border-2 border-foreground p-3 bg-card" data-testid="mc-tornado">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <p className="font-display uppercase font-bold text-xs tracking-widest">{t("Tornado · gevoeligheid per variabele")}</p>
        <p className="text-[10px] text-muted-foreground italic">
          {t("Spearman-correlatie: −1 = tegengesteld · 0 = geen invloed · +1 = versterkt")}
        </p>
      </div>
      <div style={{ width: "100%", height: maxHeight }}>
        <ResponsiveContainer>
          <BarChart data={data} layout="vertical" margin={{ top: 8, right: 32, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" horizontal={false} />
            <XAxis type="number" domain={[-1, 1]} tick={{ fontSize: 10, fontFamily: "monospace" }} />
            <YAxis type="category" dataKey="label" width={190} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => v.toFixed(3)} labelFormatter={(l) => l} />
            <ReferenceLine x={0} stroke="#000" strokeWidth={1} />
            <Bar dataKey="correlation">
              {data.map((d) => (
                <Cell key={d.key || d.label} fill={d.correlation >= 0 ? "#0047FF" : "#FF5900"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function FanChart({ t, fanChart, startYear, cashFloor }) {
  // Recharts ComposedChart: Area for P10-P90 band + Line for P50.
  // Data shape: {year, p10, p50, p90, band: [p10, p90]} for stacked area rendering.
  const data = fanChart.map((row) => ({
    year: startYear ? String(Number(startYear) + Number(row.year)) : String(row.year),
    p10: row.p10,
    p50: row.p50,
    p90: row.p90,
    range: [row.p10, row.p90],
  }));
  // Ondergrens eigen kas (alleen als een budgetafwijking meedraaide): in welke jaren duikt de kas eronder?
  const hasFloor = cashFloor != null && Number.isFinite(Number(cashFloor));
  const belowP50 = hasFloor ? data.filter((d) => d.p50 < cashFloor).map((d) => d.year) : [];
  const belowP10 = hasFloor ? data.filter((d) => d.p10 < cashFloor && d.p50 >= cashFloor).map((d) => d.year) : [];
  return (
    <div className="border-2 border-foreground p-3 bg-card" data-testid="mc-fan-chart">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <p className="font-display uppercase font-bold text-xs tracking-widest">{t("Fan-chart · kaspositie 31/12 per projectjaar")}</p>
        <p className="text-[10px] text-muted-foreground italic">
          {t("Blauwe band = P10–P90 bandbreedte · Zwarte lijn = mediaan (P50)")}{hasFloor && <> · {t("Paarse stippellijn = ondergrens eigen kas")}</>}
        </p>
      </div>
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
            <XAxis dataKey="year" tick={{ fontSize: 10, fontFamily: "monospace" }} />
            <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} tickFormatter={(v) => fmtCurrency(v)} width={72} />
            <Tooltip formatter={(v, name) => {
              if (Array.isArray(v)) return [`${fmtInt(v[0])} – ${fmtInt(v[1])}`, "P10–P90"];
              return [fmtInt(v), name];
            }} labelFormatter={(l) => `${t("Boekjaar")} ${l}`} />
            <ReferenceLine y={0} stroke="#FF5900" strokeWidth={1} />
            {hasFloor && (
              <ReferenceLine y={cashFloor} stroke="#7A1FFF" strokeWidth={2} strokeDasharray="6 4" ifOverflow="extendDomain"
                             label={{ value: `${t("Ondergrens eigen kas")} ${fmtCurrency(cashFloor)}`, position: "insideTopRight", fontSize: 10, fill: "#7A1FFF", fontWeight: 700 }} />
            )}
            <Area dataKey="range" fill="#0047FF" fillOpacity={0.15} stroke="none" name="P10–P90" />
            <Line dataKey="p50" stroke="#000" strokeWidth={2} dot={false} name="Mediaan (P50)" />
            <Line dataKey="p10" stroke="#FF5900" strokeDasharray="4 3" strokeWidth={1} dot={false} name="P10" />
            <Line dataKey="p90" stroke="#00D05A" strokeDasharray="4 3" strokeWidth={1} dot={false} name="P90" />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {hasFloor && (
        <p className="text-[11px] mt-2" data-testid="mc-fan-floor-note" style={{ color: "#7A1FFF" }}>
          {belowP50.length > 0
            ? <>{t("Mediaan (P50) onder de ondergrens in")}: <b className="font-mono-num">{belowP50.join(", ")}</b></>
            : <>{t("De mediaan (P50) blijft in alle jaren boven de ondergrens")}</>}
          {belowP10.length > 0 && <> · {t("alleen in ongunstige runs (P10) eronder in")}: <b className="font-mono-num">{belowP10.join(", ")}</b></>}
        </p>
      )}
    </div>
  );
}

function Kpi({ t, label, value, tone, suffix, testid }) {
  const arrow = tone === "up" ? <TrendingUp className="w-3.5 h-3.5 text-positive" /> :
                tone === "down" ? <TrendingDown className="w-3.5 h-3.5 text-destructive" /> : null;
  return (
    <div className="border-2 border-foreground p-3 bg-card" data-testid={testid}>
      <div className="stat-label flex items-center gap-1">{arrow}{t(label)}</div>
      <div className="font-display font-black text-xl mt-1 font-mono-num">
        {fmtCurrency(value)}{suffix && <span className="text-xs font-normal text-muted-foreground ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function RiskCard({ t, label, pct, testid }) {
  const p = parseFloat(pct);
  const bg = p >= 20 ? "bg-destructive/10 border-destructive" : p >= 5 ? "bg-secondary/10 border-secondary" : "bg-positive/10 border-positive";
  const icon = p >= 20 ? <AlertTriangle className="w-4 h-4 text-destructive" /> : null;
  return (
    <div className={`border-2 p-3 ${bg}`} data-testid={testid}>
      <div className="stat-label flex items-center gap-1">{icon}{label}</div>
      <div className="font-display font-black text-2xl mt-1 font-mono-num">{pct}%</div>
    </div>
  );
}
